<template>
  <v-container>
    <v-row>
      <v-col v-for="option in baseOptions" :key="option.name" sm="4" class="d-flex align-center">
        <select-list
          :clearable="option.clearable"
          :custom-type="option.customType"
          :disabled="isLoading || isDisabled"
          :items="option.items"
          :label="option.name"
          :limit="1"
          :loading="isLoading"
          :placeholder="`Select ${option.name.toLowerCase()}`"
          :value="value[option.model]"
          class="flex-grow-1"
          hide-details
          @input="onInput($event, option.model)"
        />
      </v-col>
      <v-col v-for="option in filterOptions" :key="option.name" sm="4" class="d-flex align-center">
        <select-list
          :clearable="option.clearable"
          :custom-type="option.customType"
          :disabled="isLoading || isDisabled"
          :items="option.items"
          :label="option.name"
          :limit="1"
          :loading="isLoading"
          :multiple="option.multiple"
          :value="value[option.model]"
          class="flex-grow-1"
          hide-details
          @input="onInput($event, option.model)"
        />
      </v-col>

      <v-spacer />

      <v-col sm="4" class="d-flex justify-end">
        <v-btn
          :disabled="isLoading"
          :loading="isLoading"
          class="ml-2"
          color="primary"
          height="40"
          block
          depressed
          @click.prevent="onClick"
        >
          {{ button }}
          <template #loader>
            <span :class="$style.loader">
              <v-icon light>cached</v-icon>
            </span>
          </template>
        </v-btn>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
// Helpers
import { mapGetters } from 'vuex'
import { assign } from 'lodash-es'
// Components
import SelectList from '@/components/forms/select-list'
// Services
import { languages } from '@/services/offers'

export default {
  name: 'PreviewFilters',
  components: {
    SelectList,
  },
  props: {
    button: {
      type: [String, Boolean],
      default: false,
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    search: {
      type: Boolean,
      default: false,
    },
    sites: {
      type: Array,
      required: true,
    },
    value: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    languageOptions: [],
  }),
  computed: {
    ...mapGetters('options', ['getItemsForOption']),
    baseOptions() {
      return [
        {
          name: 'Offer Type',
          clearable: false,
          model: 'offerType',
          items: this.getItemsForOption('offerTypes'),
        },
        {
          name: 'Language',
          clearable: false,
          model: 'language',
          items: this.languageOptions,
        },
        {
          name: 'Country',
          clearable: false,
          model: 'country',
          items: this.getItemsForOption('countries'),
          customType: 'country',
        },
        {
          name: 'Website',
          clearable: true,
          model: 'site',
          items: this.sites
            .filter(({ department }) => this.value.department === department)
            .map(({ name: key, hostname: value, assets: { favicon } = {} }) => ({ key, value, icon: favicon })),
          customType: 'avatar',
        },
      ]
    },
    filterOptions() {
      return [
        {
          name: 'Department',
          model: 'department',
          items: this.getItemsForOption('departments').filter(({ value }) =>
            ['organic', 'performance'].includes(value)
          ),
        },
        {
          name: 'Coins',
          clearable: true,
          model: 'coins',
          multiple: true,
          items: this.getItemsForOption('coins'),
          customType: 'symbol',
        },
        {
          name: 'Payment Methods',
          clearable: true,
          model: 'paymentMethods',
          multiple: true,
          items: this.getItemsForOption('paymentMethods'),
        },
        {
          name: 'Stocks',
          clearable: true,
          model: 'stocks',
          multiple: true,
          items: this.getItemsForOption('stocks'),
        },
        {
          name: 'Allow Swaps',
          model: 'allowSwaps',
          items: [
            { key: 'Yes', value: true },
            { key: 'No', value: false },
          ],
        },
      ]
    },
  },
  async created() {
    await this.fetchLanguageOptions()
  },
  methods: {
    async fetchLanguageOptions() {
      try {
        this.languageOptions = (await languages.getLanguages()).map(({ name, code }) => ({ key: name, value: code }))
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to fetch translation language options',
          duration: -1,
          data: { error },
        })
      }
    },
    onInput(value, key) {
      const updatedQuery = assign(this.value, { [key]: value })
      this.$emit('input', updatedQuery)
    },
    onClick() {
      this.$emit('update-filters')
    },
  },
}
</script>

<style lang="scss" module>
.loader {
  animation: loader 1s infinite;
  display: flex;
}

@keyframes loader {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
